/**
 * @fileoverview Material-UI Typography component wrapper for consistent text display.
 * Provides a standardized way to display labeled text with optional links and date formatting.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import * as React from 'react';
import { Typography, Link } from '@mui/material';
import { formatDate, formatDateTime } from '../../../../components/ex_dayjs';
import { IData } from '../../../../types';

/**
 * Material-UI Typography wrapper component for displaying labeled content with various formats.
 * Supports plain text, links, and formatted dates/datetimes with consistent styling.
 *
 * @param {Object} props - Component properties
 * @param {string} props.label - The label text to display before the content
 * @param {string} [props.name] - The main content text to display
 * @param {string} [props.href] - Optional URL to make the content a clickable link
 * @param {string|Date} [props.date] - Optional date to format and display
 * @param {string|Date} [props.datetime] - Optional datetime to format and display
 * @param {string} [props.bold=''] - CSS font-weight value for date formatting (e.g., 'bold', 'normal')
 * @returns {JSX.Element} Typography component with formatted content
 *
 * @example
 * // Display a simple label and text
 * <MTypography label="Order Number" name="12345" />
 *
 * @example
 * // Display a label with a clickable link
 * <MTypography label="Event Link" name="Concert Details" href="https://example.com/event" />
 *
 * @example
 * // Display a formatted date
 * <MTypography label="Event Date" date={new Date()} bold="bold" />
 */
export const MTypography: React.FC<IData> = ({ label, name, href, date, datetime, bold = '' }) => {
    return (
        <Typography>
            <label style={{ fontWeight: 'bold' }}>{label + ': '}</label>
            {href && (
                <Link target='_blank' href={href} >{name}</Link>
            )}
            {date && (
                <label style={{ fontWeight: bold }}>{formatDate(date)}</label>
            )}
            {datetime && (
                <label>{formatDateTime(datetime)}</label>
            )}
            {!href && !datetime && !date && name && (
                <label>{name}</label>
            )}
        </Typography>
    );
};